// DTOs de S2 — detalle de línea del supervisor.
//
//   - GET /api/supervisor/linea/:lineaId
//   - PATCH /api/registros/:id/validar
//   - PATCH /api/registros/:id/invalidar
//
// Reusa supervisorKpiLineaSchema de S1 + registroProduccionHoraSchema.
import { z } from 'zod';
import { lineaSchema } from './linea.js';
import { plantaSchema } from './planta.js';
import { registroProduccionHoraSchema } from './registro_produccion_hora.js';
import { supervisorKpiLineaSchema, turnoObjetivoSchema } from './supervisor_dashboard.js';
import { paroSupervisorDetalleSchema } from './supervisor_paros.js';
/**
 * Un bloque (registro de 1 hora) con metadatos derivados para la UI del
 * supervisor: si está validado, si el supervisor puede invalidarlo.
 */
export const supervisorBloqueSchema = z.object({
    registro: registroProduccionHoraSchema,
    es_validado: z.boolean(),
    /**
     * El supervisor puede invalidar SI el registro está en estado='valido'.
     * El backend ya hizo el check de scope (la planta de la línea está en
     * usuarios_supervisor_plantas del supervisor logueado), así que para la UI
     * basta con este flag. Si false → botón Invalidar disabled con tooltip.
     */
    puede_invalidar: z.boolean(),
});
export const supervisorLineaDetalleSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    linea: lineaSchema.extend({
        planta: plantaSchema.pick({ id: true, nombre: true, codigo: true }),
        area_nombre: z.string(),
    }),
    turno_objetivo: turnoObjetivoSchema,
    /**
     * Programación del líder para esta línea HOY. Si activa=false, no hay líder
     * asignado y los bloques del turno no tienen `lider` (solo capturado_por).
     */
    programacion_lider_hoy: z.object({
        activa: z.boolean(),
        lider: z
            .object({
            id: z.string().uuid(),
            nombre: z.string(),
            apellido: z.string(),
            email: z.string().email(),
        })
            .nullable(),
        es_linea_principal: z.boolean().nullable(),
    }),
    /** KPI agregado del turno (mismo cálculo que S1 card). */
    kpi_linea: supervisorKpiLineaSchema.nullable(),
    /** Lista de bloques (registros) del turno_objetivo, ordenados por hora_inicio_bloque ASC. */
    bloques: z.array(supervisorBloqueSchema),
    /**
     * Diccionario paro_id → detalle enriquecido (nombres de causal y categoría,
     * sellos del ciclo técnico/supervisor, corrección viva, flags de acción) de
     * TODOS los paros presentes en los bloques.
     *
     * Va como hermano y no dentro de `registro.paros` para que
     * `registroProduccionHoraSchema` conserve su shape canónico — mismo patrón
     * que los diccionarios `skus`/`causales` de `coordinador_linea_detalle.ts`.
     */
    paros_detalle: z.record(z.string().uuid(), paroSupervisorDetalleSchema),
    hora_servidor: z.string(),
    fecha_hoy: z.string(),
    /**
     * Fecha OPERATIVA del turno_objetivo (día en que arrancó). En la cola
     * post-medianoche del T3 es fecha_hoy - 1. S5 (delegación) captura con
     * ESTA fecha, no con fecha_hoy.
     */
    fecha_operativa: z.string(),
});
// ============================================================================
// PATCH /api/registros/:id/invalidar — body
// ============================================================================
export const invalidarRegistroSchema = z.object({
    razon_invalidacion: z
        .string()
        .trim()
        .min(20, 'Razón obligatoria de mínimo 20 caracteres')
        .max(500),
});
//# sourceMappingURL=supervisor_linea_detalle.js.map